import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import { LoginPage } from './components/LoginPage';
import RedeemTrialPage from './components/RedeemTrialPage';
import { Dashboard } from './components/Dashboard';
import UnifiedWorkspace from './components/UnifiedWorkspace';
import { AnalysisWorkspace } from './components/AnalysisWorkspace';
import RedesignWorkspace from './components/RedesignWorkspace';
import CreatePPTWorkspace from './components/CreatePPTWorkspace';
import InfographicWorkspace from './components/InfographicWorkspace';
import DocumentWorkspace from './components/DocumentWorkspace';
import DashboardWorkspace from './components/DashboardWorkspace';
import SheetsWorkspace from './components/SheetsWorkspace';
import { EnhancePage } from './components/EnhancePage';
import { EnsurePage } from './components/EnsurePage';
import { KnowledgeBasePage } from './components/KnowledgeBasePage';
import { AppSidebar } from './components/AppSidebar';
import { AIChat } from './components/AIChat';
import { UploadPage } from './components/UploadPage';
import SharedReportView from './components/SharedReportView';
import SharedContentView from './components/SharedContentView';
import SharedBrandView from './components/SharedBrandView';
import PlanningWorkspace from './components/PlanningWorkspace';
import LandingB2C from './components/LandingB2C';
import LandingB2B from './components/LandingB2B';
import { GenerationProgressView } from './components/GenerationProgressView';
import EmailConfirmationLock from './components/EmailConfirmationLock';

import { BrandIdentitiesPage } from './components/BrandIdentitiesPage';
import { ProjectsPage } from './components/ProjectsPage';
import { RecentsPage } from './components/RecentsPage';
import NativeTemplateLab from './components/NativeTemplateLab';
import AdminDashboard from './components/AdminDashboard';
import { ThemeProvider } from './components/theme-provider';
import { ToastProvider } from './components/toast-provider';
import { supabaseHelpers, supabase } from './lib/supabase';
import { toast } from 'sonner';
import NamePromptModal from './components/NamePromptModal';
import { withBackend, normalizeImageUrl } from './utils/api';
import { authHeaders } from './utils/authHeaders';

// r113b: the page component is retired; Settings renders AccountSummary /
// AppConnections / PersonalAccessTokens from this module directly.
function ProfileRedirect({ onOpenSettings }) {
  React.useEffect(() => { onOpenSettings(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

const INITIAL_PROJECTS = [];

// Map internal state names to URL paths
const STATE_TO_URL = {
  'login': '/login',
  'dashboard': '/',
  'upload': '/upload',
  'agentic-flow': '/create/presentation',
  'proposal-flow': '/create/proposal',
  'infographic-flow': '/create/infographic',
  'document-flow': '/create/document',
  'dashboard-flow': '/create/dashboard',
  'sheets-flow': '/create/sheets',
  'planning-flow': '/create/plan',
  'redesign': '/create/redesign',
  'create-ppt': '/create/presentation',
  'enhance': '/enhance',
  'ensure': '/ensure',
  'knowledge': '/knowledge',
  'brand-identities': '/brand-identities',
  'projects-list': '/projects',
  'recents': '/recents',
  'native-template-lab': '/labs/native-template',
  'profile': '/profile',
  'admin': '/admin',
  'landing-b2c': '/landingb2c',
  'landing-b2b': '/landingb2b',
};

// Map URL paths back to internal state names
function urlToState(pathname) {
  if (pathname === '/login' || pathname === '/signup' || pathname === '/thankyou') return 'login';
  if (pathname === '/ar/login' || pathname === '/ar/signup' || pathname === '/ar/thankyou') return 'login';
  if (pathname === '/' || pathname === '') return 'dashboard';
  if (pathname === '/upload') return 'upload';
  if (pathname === '/create/presentation') return 'agentic-flow';
  if (pathname === '/create/proposal') return 'proposal-flow';
  if (pathname === '/create/infographic') return 'infographic-flow';
  if (pathname === '/create/document') return 'document-flow';
  if (pathname === '/create/dashboard') return 'dashboard-flow';
  if (pathname === '/create/sheets') return 'sheets-flow';
  if (pathname === '/create/plan') return 'planning-flow';
  if (pathname === '/create/redesign') return 'redesign';
  if (pathname === '/enhance') return 'enhance';
  if (pathname === '/ensure') return 'ensure';
  if (pathname === '/knowledge') return 'knowledge';
  if (pathname === '/brand-identities') return 'brand-identities';
  if (pathname === '/projects') return 'projects-list';
  if (pathname === '/recents') return 'recents';
  if (pathname === '/labs/native-template') return 'native-template-lab';
  if (pathname === '/profile') return 'profile';
  if (pathname === '/admin') return 'admin';
  if (pathname === '/landingb2c') return 'landing-b2c';
  if (pathname === '/landingb2b') return 'landing-b2b';
  if (pathname.startsWith('/project/')) return 'project-view';
  if (pathname.startsWith('/report/')) return 'shared-report';
  if (pathname.startsWith('/share/')) return 'shared-content';
  if (pathname.startsWith('/brand/share/')) return 'shared-brand';
  if (pathname.startsWith('/redeem/') || pathname.startsWith('/ar/redeem/')) return 'redeem';
  if (pathname === '/oauth/callback') return 'oauth-callback';
  return 'dashboard';
}

function OAuthCallbackPage() {
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const provider = params.get('provider');
    const status = params.get('status');
    const message = params.get('message') || '';
    if (window.opener) {
      window.opener.postMessage({ type: 'OAUTH_CALLBACK', provider, status, message }, '*');
      window.close();
    }
  }, []);
  return (
    <div className="flex items-center justify-center h-screen text-muted-foreground text-sm">
      Completing authorization...
    </div>
  );
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const isNavigatingRef = useRef(false);

  const [currentState, setCurrentState] = useState('loading'); // Start with loading to check session
  const [projects, setProjects] = useState(INITIAL_PROJECTS);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  // Initialize projectAnalyses from localStorage
  const [projectAnalyses, setProjectAnalyses] = useState(() => {
    try {
      const saved = localStorage.getItem('prezlab_project_analyses');
      if (!saved) return {};
      const parsed = JSON.parse(saved);
      // Clean up entries corrupted by earlier html_content stripping bug
      for (const key of Object.keys(parsed)) {
        const entry = parsed[key];
        if (entry?.documentData && !entry.documentData.html_content) delete entry.documentData;
        if (entry?.infographicData && !entry.infographicData.html_content) delete entry.infographicData;
        if (entry?.dashboardData && !entry.dashboardData.html_content) delete entry.dashboardData;
      }
      return parsed;
    } catch (err) {
      console.error('Failed to load project analyses from localStorage:', err);
      return {};
    }
  });

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatExpanded, setIsChatExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [userName, setUserName] = useState(null);
  const [hideGlobalNav, setHideGlobalNav] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('prezlab_sidebar_collapsed') === 'true'; } catch { return false; }
  });
  const [agenticFlowResult, setAgenticFlowResult] = useState(null);
  const [usageData, setUsageData] = useState(null);
  // r113b: /profile is retired — its content all lives in Settings -> Account
  // now. App owns this so an old bookmark can open Settings instead of
  // rendering a second, drifting copy of the same page.
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Folder the sidebar asked the Projects page to open directly.
  const [projectsInitialFolder, setProjectsInitialFolder] = useState(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  // Single active workspace (one per browser tab)
  const [activeWorkspace, setActiveWorkspace] = useState(null);

  const TAB_TYPE_LABELS = {
    'agentic-flow': 'Slides',
    'proposal-flow': 'Proposal',
    'infographic-flow': 'Infographic',
    'document-flow': 'Document',
    'dashboard-flow': 'Dashboard',
    'sheets-flow': 'Spreadsheet',
    'planning-flow': 'AI Consultant',
  };

  // Navigation helper: updates both internal state and URL
  const navigateTo = useCallback((state, { projectId, replace = false } = {}) => {
    isNavigatingRef.current = true;
    setCurrentState(state);

    // Don't push URL for transitional states
    if (state === 'loading') return;

    // Auth pages — /login, /signup, /ar/login, /ar/signup all map to
    // the 'login' state. Preserve whichever URL the user landed on so
    // the language toggle and the signup vs sign-in default both work.
    const AUTH_PATHS = ['/login', '/signup', '/thankyou', '/ar/login', '/ar/signup', '/ar/thankyou'];
    if (state === 'login' && AUTH_PATHS.includes(location.pathname)) {
      setTimeout(() => { isNavigatingRef.current = false; }, 0);
      return;
    }

    let url;
    if (projectId) {
      // Saved project — URL is always /project/:id regardless of workspace type
      url = `/project/${projectId}`;
    } else {
      url = STATE_TO_URL[state] || '/';
    }

    if (location.pathname !== url) {
      navigate(url, { replace });
    }

    // Reset flag after React processes the navigation
    setTimeout(() => { isNavigatingRef.current = false; }, 0);
  }, [navigate, location.pathname]);

  // Sync browser back/forward to internal state
  useEffect(() => {
    if (isNavigatingRef.current) return;
    if (currentState === 'loading') return; // Don't override during initial load

    const newState = urlToState(location.pathname);

    if (newState === 'project-view') {
      const projectId = location.pathname.split('/project/')[1]?.split('/')[0];
      if (projectId && projectId !== selectedProjectId) {
        handleProjectSelect(projectId);
      }
    } else if (newState !== currentState) {
      setCurrentState(newState);
      // Reset workspace state when navigating back to dashboard
      if (newState === 'dashboard') {
        setHideGlobalNav(false);
        setSelectedProjectId(null);
      }
      // Show nav for non-workspace states
      if (['dashboard', 'upload', 'enhance', 'ensure', 'knowledge', 'profile'].includes(newState)) {
        setHideGlobalNav(false);
      }
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for auto-analyze parameters and existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      // Check if this is a shared report view (supports both /report/:hash and ?report=hash)
      const urlParams = new URLSearchParams(window.location.search);
      const reportHash = urlParams.get('report');
      const isReportPath = window.location.pathname.startsWith('/report/');

      if (reportHash) {
        // Legacy ?report=hash format — redirect to /report/:hash
        const hash = reportHash;
        navigate(`/report/${hash}`, { replace: true });
        setCurrentState('shared-report');
        return;
      }
      if (isReportPath) {
        setCurrentState('shared-report');
        return;
      }

      // Check if this is a shared content view (/share/:hash)
      if (window.location.pathname.startsWith('/share/')) {
        setCurrentState('shared-content');
        return;
      }

      // r169: team invite LINK (?team_invite=CODE). Stash the code so a login
      // bounce can't lose it, strip the param, then continue the normal flow —
      // the redemption effect claims it once a user is signed in.
      const teamInvite = (urlParams.get('team_invite') || '').trim();
      if (teamInvite) {
        try { localStorage.setItem('pending_team_invite', teamInvite); } catch {}
        try {
          urlParams.delete('team_invite');
          const qs = urlParams.toString();
          window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
        } catch {}
      }

      // Trial redemption link (/redeem/:code) — public landing. Stash the code
      // immediately so a login bounce can't lose it, and still restore an
      // existing session so a signed-in user redeems right away. RedeemTrialPage
      // shows the login/signup UI when signed out.
      if (window.location.pathname.replace(/^\/ar(?=\/)/, '').startsWith('/redeem/')) {
        setCurrentState('redeem');
        try {
          const m = window.location.pathname.replace(/^\/ar(?=\/)/, '').match(/\/redeem\/([^/?#]+)/);
          // localStorage (not session) so the code survives the email-confirm
          // round-trip, which lands in a fresh tab with its own sessionStorage.
          if (m) localStorage.setItem('pending_trial_code', m[1].trim().toLowerCase());
        } catch {}
        try {
          const { user, error } = await supabaseHelpers.getCurrentUser();
          if (user && !error) setCurrentUser(user);
        } catch {}
        return;
      }

      // Admin panel — standalone auth, bypass normal session check
      if (window.location.pathname === '/admin') {
        setCurrentState('admin');
        return;
      }

      // Landing pages — public, no auth needed
      if (window.location.pathname === '/landingb2c') {
        setCurrentState('landing-b2c');
        return;
      }
      if (window.location.pathname === '/landingb2b') {
        setCurrentState('landing-b2b');
        return;
      }

      // Check if this is an auto-analyze request from PowerPoint add-in
      const token = urlParams.get('token');
      const fileId = urlParams.get('fileId');

      if (token && fileId) {
        // Auto-analyze flow from PowerPoint add-in
        setCurrentState('loading');
        toast.info('Processing auto-login from PowerPoint add-in...');

        try {
          // Validate token with backend
          const response = await fetch(withBackend('/api/auth/validate-token'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
          });

          if (!response.ok) {
            throw new Error('Invalid or expired token');
          }

          const { email } = await response.json();

          // Check if user is already logged in
          const { user, error } = await supabaseHelpers.getCurrentUser();

          if (user && user.email === email) {
            // User already logged in with correct account
            toast.success('Auto-login successful!');

            // Fetch file from backend and create project
            toast.info('Downloading presentation from PowerPoint...');

            const fileResponse = await fetch(withBackend(`/api/files/${fileId}`));
            if (!fileResponse.ok) {
              throw new Error('Failed to retrieve uploaded file');
            }

            const blob = await fileResponse.blob();
            const filename = fileResponse.headers.get('Content-Disposition')?.match(/filename="?(.+)"?/)?.[1] || 'presentation.pptx';
            const file = new File([blob], filename, {
              type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            });

            toast.success('File downloaded! Creating project...');

            // Set user first
            setCurrentUser(user);

            // Load user's projects
            const { data: projectsData } = await supabaseHelpers.getProjects(user.id);
            if (projectsData) {
              setProjects(projectsData);
            }

            // Create project directly (same logic as handleUploadComplete)
            const nameFromFile = filename.replace(/\.[^/.]+$/, '');
            let newProjectId = `project-${Date.now()}`;

            const { data, error } = await supabaseHelpers.createProject({
              user_id: user.id,
              name: nameFromFile,
              client_name: 'Client TBD',
              status: 'in_progress',
              slide_count: 0,
            });

            if (error) {
              console.error('Error creating project in Supabase:', error);
              toast.error('Failed to save project');
            } else if (data) {
              newProjectId = data.id;
              toast.success('Project created!');
            }

            const uploadData = {
              file: file,
              filename: filename,
              audienceInfo: {
                audienceType: '',
                presentationGoal: '',
                audienceSize: '',
                researchClient: '',
                additionalContext: 'Uploaded from PowerPoint add-in',
                hasVideos: false,
              },
            };

            const newProject = {
              id: newProjectId,
              name: nameFromFile,
              client: 'Client TBD',
              status: 'in-progress',
              lastAnalyzed: null,
              owner: 'You',
              slideCount: null,
              score: null,
              uploadedFile: file,
              uploadMetadata: uploadData,
            };

            setProjects((prev) => [newProject, ...prev]);
            setProjectAnalyses((prev) => ({
              ...prev,
              [newProjectId]: {
                ...(prev[newProjectId] || {}),
                analysis: prev[newProjectId]?.analysis ?? null,
                uploadMetadata: uploadData,
                file: file,
              },
            }));

            setSelectedProjectId(newProjectId);
            navigateTo('analysis', { projectId: newProjectId, replace: true });
          } else {
            // User not logged in or wrong account
            toast.error('Please log in with your PowerPoint add-in email first');
            navigateTo('login');
          }
        } catch (err) {
          console.error('Auto-analyze error:', err);
          toast.error('Auto-login failed: ' + err.message);
          navigateTo('login');
        }

        return;
      }

      // Normal session check (no auto-analyze params)
      const rememberMe = localStorage.getItem('prezlab_remember_me') === 'true';
      if (!rememberMe) {
        navigateTo('login');
        return;
      }

      const { user, error } = await supabaseHelpers.getCurrentUser();
      if (user && !error) {
        // Restore session and navigation state from URL
        await handleLogin(user, true);
      } else {
        navigateTo('login');
      }
    };

    checkSession();
  }, []);

  // Listen for PASSWORD_RECOVERY event from Supabase (triggered by reset link)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setShowPasswordReset(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const selectedProject = useMemo(
    () => projects.find((project) => project.id === selectedProjectId) || null,
    [projects, selectedProjectId]
  );

  // URL is now the source of truth for navigation state — no localStorage persistence needed

  // Save selected project ID to localStorage whenever it changes
  useEffect(() => {
    if (selectedProjectId) {
      localStorage.setItem('prezlab_selected_project', selectedProjectId);
    } else {
      localStorage.removeItem('prezlab_selected_project');
    }
  }, [selectedProjectId]);

  // Save projectAnalyses to localStorage and Supabase whenever it changes
  const localStorageSkipRef = useRef(false);
  useEffect(() => {
    // Save to localStorage (backup) — skip if previous attempt hit quota
    if (!localStorageSkipRef.current) {
      try {
        const serializable = Object.keys(projectAnalyses).reduce((acc, key) => {
          const analysis = projectAnalyses[key];
          if (analysis) {
            // Deep clean to strip base64 data and large HTML content
            const cleaned = {
              ...analysis,
              file: undefined,
            };

            // Strip base64 from createPPTData slides to save space
            if (cleaned.createPPTData?.slides) {
              cleaned.createPPTData = {
                ...cleaned.createPPTData,
                slides: cleaned.createPPTData.slides.map(slide => ({
                  ...slide,
                  preview_url: slide.preview_url?.startsWith('http') ? slide.preview_url : undefined,
                  preview_base64: undefined,
                  html_source: undefined,
                  extractedLayers: slide.extractedLayers ? {
                    hasLayers: true,
                    layerCount: slide.extractedLayers.layers?.length || 0,
                    source: slide.extractedLayers.source
                  } : undefined
                }))
              };
            }

            acc[key] = cleaned;
          }
          return acc;
        }, {});
        localStorage.setItem('prezlab_project_analyses', JSON.stringify(serializable));
      } catch (err) {
        console.error('Failed to save project analyses to localStorage:', err);
        if (err.name === 'QuotaExceededError') {
          console.warn('localStorage quota exceeded — disabling localStorage cache for this session');
          localStorageSkipRef.current = true;
          try { localStorage.removeItem('prezlab_project_analyses'); } catch {}
        }
      }
    }

    // Save to Supabase (primary storage)
    if (currentUser && currentUser.id) {
      Object.keys(projectAnalyses).forEach(async (projectId) => {
        const projectData = projectAnalyses[projectId];
        if (projectData && projectData.analysis) {
          try {
            await supabaseHelpers.updateProject(projectId, {
              analysis_data: projectData.analysis,
              upload_metadata: projectData.uploadMetadata,
              file_hash: projectData.analysis.file_hash || projectData.analysis.metadata?.file_hash,
              overall_score: projectData.analysis.presentationScore?.overall,
              slide_count: projectData.analysis.slides?.length,
            });
          } catch (err) {
            console.error(`Failed to save project ${projectId} to Supabase:`, err);
          }
        }
      });
    }
  }, [projectAnalyses, currentUser]);

  // Fetch usage data for the current user
  const fetchUsage = useCallback(async (uid, email) => {
    try {
      const resp = await fetch(withBackend('/api/usage/status'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: uid, email: email || '' }),
      });
      if (resp.ok) {
        const data = await resp.json();
        setUsageData(data);
      }
    } catch (err) {
      console.warn('Failed to fetch usage data:', err);
    }
  }, []);

  // Fallback trial redemption: if a /redeem/<code> link bounced through signup
  // (RedeemTrialPage unmounted on post-login navigation), claim the stashed code
  // once the user is in. Skipped while on the redeem page itself, which handles
  // its own claim. The redeem endpoint is idempotent, so this never double-grants.
  useEffect(() => {
    if (!currentUser?.id || currentState === 'redeem') return;
    let pending = '';
    try { pending = localStorage.getItem('pending_trial_code') || ''; } catch {}
    if (!pending) return;
    (async () => {
      try {
        const resp = await fetch(withBackend('/api/trial/redeem'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: currentUser.id, code: pending }),
        });
        const data = await resp.json().catch(() => ({}));
        try { localStorage.removeItem('pending_trial_code'); } catch {}
        if (resp.ok && data.success) {
          toast.success('Your 7-day trial is active!');
          fetchUsage(currentUser.id, currentUser.email);
        }
      } catch {}
    })();
  }, [currentUser, currentState, fetchUsage]);

  // r169: team invite links — once a user is signed in, claim any stashed
  // invite code over the STRICT bearer route (mirrors the Team page's own
  // join flow). The stash is cleared before branching, so one link gets one
  // attempt — a failure surfaces as a toast, never a retry loop.
  useEffect(() => {
    if (!currentUser?.id) return;
    let pending = '';
    try { pending = localStorage.getItem('pending_team_invite') || ''; } catch {}
    if (!pending) return;
    // r169b (review): clear the stash SYNCHRONOUSLY — the effect can fire
    // twice (session restore + login identity change), and two in-flight
    // claims meant a success toast followed by a spurious 409 error toast.
    try { localStorage.removeItem('pending_team_invite'); } catch {}
    (async () => {
      try {
        const resp = await fetch(withBackend('/api/teams/join'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
          body: JSON.stringify({ code: pending }),
        });
        const data = await resp.json().catch(() => ({}));
        if (resp.ok) {
          toast.success(`Welcome to ${data.team?.name || 'the team'} — your plan is now ${data.team?.tier || 'team'}.`);
          fetchUsage(currentUser.id, currentUser.email);
        } else if (data.error) {
          toast.error(`Team invite: ${data.error}`);
        }
      } catch {}
    })();
  }, [currentUser, fetchUsage]);

  const handleLogin = async (user, restoreState = false) => {
    setIsLoading(true);
    setCurrentUser(user);

    // Check if user has a display name set
    const displayName = user?.user_metadata?.display_name;
    if (displayName) {
      setUserName(displayName);
    } else {
      // Show name prompt for users without a display name
      setShowNamePrompt(true);
    }

    // Load user's projects from Supabase (lightweight query - no heavy JSONB data)
    if (user && user.id) {
      const { data, error } = await supabaseHelpers.getProjects(user.id);
      if (error) {
        console.error('Error loading projects:', error);
        toast.error('Failed to load projects');
      } else if (data) {
        console.log('📊 Loaded projects from Supabase:', data);
        // Convert Supabase projects to app format
        const statusMap = {
          'in_progress': 'in-progress',
          'needs_review': 'needs-review',
          'completed': 'completed',
          'draft': 'draft',
          'archived': 'archived'
        };

        const loadedProjects = data.map(project => {
          // Use cached projectAnalyses from localStorage to determine type
          const cached = projectAnalyses[project.id];
          let projectType = 'analysis';
          if (cached?.redesignData) {
            projectType = 'redesign';
          } else if (cached?.createPPTData) {
            projectType = 'create-ppt';
          } else if (cached?.infographicData) {
            projectType = 'infographic';
          } else if (cached?.documentData) {
            projectType = 'document';
          } else if (cached?.dashboardData) {
            projectType = 'dashboard';
          } else if (cached?.sheetsData) {
            projectType = 'sheets';
          }
          // Also check client_name as fallback for type detection
          if (projectType === 'analysis') {
            if (project.client_name === 'Create PPT') projectType = 'create-ppt';
            else if (project.client_name === 'Infographic') projectType = 'infographic';
            else if (project.client_name === 'Document') projectType = 'document';
            else if (project.client_name === 'Dashboard') projectType = 'dashboard';
            else if (project.client_name === 'Sheets') projectType = 'sheets';
            else if (project.client_name === 'Native PPT') projectType = 'native-ppt';
          }

          return {
            id: project.id,
            name: project.name,
            client: project.client_name || 'Client TBD',
            status: statusMap[project.status] || 'draft',
            lastAnalyzed: project.last_analyzed,
            slideCount: project.slide_count,
            score: project.overall_score || null,
            thumbnail: normalizeImageUrl(project.thumbnail_url),
            type: projectType,
            is_shared: project.is_shared || false,
            shared_role: project.shared_role || null,
          };
        });
        console.log('✅ Converted projects:', loadedProjects);
        setProjects(loadedProjects);
      }
      // Fetch usage/tier data
      fetchUsage(user.id, user.email);
    }

    // Restore navigation state from the current URL (not localStorage)
    if (restoreState) {
      const urlState = urlToState(window.location.pathname);

      if (urlState === 'project-view') {
        // URL has /project/:id — load the project from Supabase
        const projectId = window.location.pathname.split('/project/')[1]?.split('/')[0];
        if (projectId) {
          // handleProjectSelect will set the right state + load data
          // Pass user directly since React state hasn't settled yet
          setIsLoading(false);
          handleProjectSelect(projectId, user);
          return;
        }
        navigateTo('dashboard');
      } else if (urlState === 'shared-report') {
        setCurrentState('shared-report');
      } else if (urlState === 'shared-content') {
        setCurrentState('shared-content');
      } else if (urlState === 'shared-brand') {
        setCurrentState('shared-brand');
      } else if (urlState === 'landing-b2c' || urlState === 'landing-b2b') {
        setCurrentState(urlState);
      } else if (urlState !== 'login' && urlState !== 'loading') {
        // For create flows, URL is preserved — workspace starts fresh (prompt phase)
        setCurrentState(urlState);
        // Keep the global sidebar expanded for workspace flows too (incl. on a
        // direct/refresh URL load); only the fullscreen editor collapses it.
      } else {
        navigateTo('dashboard');
      }
    } else {
      navigateTo('dashboard');
    }

    setIsLoading(false);
  };

  const handleNameSubmit = async (name) => {
    try {
      const { user, error } = await supabaseHelpers.updateUserName(name);
      if (error) {
        console.error('Error updating user name:', error);
        toast.error('Failed to save your name');
        return;
      }

      setUserName(name);
      setShowNamePrompt(false);
      toast.success(`Welcome, ${name}!`);
    } catch (error) {
      console.error('Error submitting name:', error);
      toast.error('Something went wrong');
    }
  };

  const handleProjectSelect = async (projectId, userOverride = null) => {
    const effectiveUser = userOverride || currentUser;
    setIsLoading(true);
    setSelectedProjectId(projectId);

    // Find the project to check its type
    const selectedProject = projects.find(p => p.id === projectId);
    let projectType = selectedProject?.type || 'analysis';

    // Track freshly loaded data (setProjectAnalyses is async, so projectAnalyses is stale in this function)
    let freshData = {};

    // Load data from Supabase if it exists
    if (effectiveUser && effectiveUser.id) {
      // Always load full project data if we don't know the type,
      // Fetch from Supabase if not cached, or if cached data has missing html_content
      // (can happen if localStorage was corrupted by earlier stripping bug)
      const cached = projectAnalyses[projectId];
      const cachedHtmlMissing = cached && (
        (cached.documentData && !cached.documentData.html_content) ||
        (cached.infographicData && !cached.infographicData.html_content) ||
        (cached.dashboardData && !cached.dashboardData.html_content) ||
        (cached.createPPTData?.slides?.length > 0 && !cached.createPPTData.slides[0]?.html_source)
      );
      // Layer B: if the cache says this presentation was still generating
      // when we last looked, force a refetch so we pick up the new
      // status='completed' (or 'failed') from Supabase and route correctly.
      const cachedStillGenerating = cached?.createPPTData?.metadata?.generating === true;
      if (!selectedProject || !cached || cachedHtmlMissing || cachedStillGenerating) {
        const { data: fullProject, error: fullErr } = await supabaseHelpers.getProjectFull(projectId);
        if (!fullErr && fullProject) {
          // r149: lazy thumbnail heal — some generation paths never wrote
          // projects.thumbnail_url (the icon-only Recents cards). Backfill
          // from the first slide preview whenever a healable project opens.
          try {
            if (!fullProject.thumbnail_url) {
              const _slides = fullProject.analysis_data?.slides || [];
              const _pv = _slides.map(s => s?.preview_url)
                .find(u => u && (String(u).startsWith('http') || String(u).startsWith('data:')));
              if (_pv) {
                supabase.from('projects').update({ thumbnail_url: _pv }).eq('id', projectId)
                  .then(() => {}, () => {});
                setProjects(prev => prev.map(p => p.id === projectId ? { ...p, thumbnail: normalizeImageUrl(_pv) } : p));
              }
            }
          } catch { /* cosmetic */ }
          // Only route to GenerationProgressView for the failure case
          // (where the user needs the retry button + clear "interrupted"
          // messaging). For in-progress decks we now fall through to the
          // regular workspace, which polls Supabase for new slides and
          // shows them as they arrive — keeps the user in one place
          // instead of bouncing them to a dedicated waiting page.
          if (
            fullProject.analysis_data?.type === 'create-ppt' &&
            fullProject.status === 'failed'
          ) {
            setIsLoading(false);
            navigateTo('generation-progress', { projectId });
            return;
          }
          // Determine actual type from full data
          if (fullProject.redesign_data) {
            projectType = 'redesign';
          } else if (fullProject.analysis_data?.type === 'create-ppt') {
            projectType = 'create-ppt';
          } else if (fullProject.analysis_data?.type === 'infographic') {
            projectType = 'infographic';
          } else if (fullProject.analysis_data?.type === 'document') {
            projectType = 'document';
          } else if (fullProject.analysis_data?.type === 'dashboard') {
            projectType = 'dashboard';
          } else if (fullProject.analysis_data?.type === 'sheets') {
            projectType = 'sheets';
          } else if (fullProject.analysis_data?.type === 'native-ppt') {
            projectType = 'native-ppt';
          }

          // Update project type in the list
          setProjects(prev => prev.map(p =>
            p.id === projectId ? { ...p, type: projectType } : p
          ));

          // Populate projectAnalyses from full data
          if (fullProject.analysis_data) {
            if (fullProject.analysis_data.type === 'create-ppt') {
              freshData.createPPTData = {
                slides: fullProject.analysis_data.slides,
                metadata: fullProject.analysis_data.metadata,
                title: fullProject.name
              };
            } else if (fullProject.analysis_data.type === 'infographic') {
              freshData.infographicData = fullProject.analysis_data;
            } else if (fullProject.analysis_data.type === 'document') {
              freshData.documentData = fullProject.analysis_data;
            } else if (fullProject.analysis_data.type === 'dashboard') {
              freshData.dashboardData = fullProject.analysis_data;
            } else if (fullProject.analysis_data.type === 'sheets') {
              freshData.sheetsData = fullProject.analysis_data;
            } else if (fullProject.analysis_data.type === 'native-ppt') {
              freshData.nativePptData = fullProject.analysis_data;
            } else {
              freshData.analysis = fullProject.analysis_data;
            }
          }
          if (fullProject.redesign_data) {
            freshData.redesignData = fullProject.redesign_data;
          }
          if (Object.keys(freshData).length > 0) {
            setProjectAnalyses(prev => ({ ...prev, [projectId]: { ...prev[projectId], ...freshData } }));
          }
        }
      }

      if (projectType === 'redesign') {
        // Load redesign data if not already loaded
        if (!projectAnalyses[projectId]?.redesignData) {
          const { data: redesignData, error } = await supabaseHelpers.getRedesignData(projectId);
          if (!error && redesignData) {
            setProjectAnalyses((prev) => ({
              ...prev,
              [projectId]: {
                ...prev[projectId],
                redesignData: redesignData,
              },
            }));
          }
        }
      } else if (projectType === 'analysis') {
        // Load analysis data for regular analysis projects
        if (!projectAnalyses[projectId]?.analysis) {
          const { data, error } = await supabaseHelpers.getAnalysisResults(projectId);
          if (!error && data && data.length > 0) {
            const latestAnalysis = data[0];
            setProjectAnalyses((prev) => ({
              ...prev,
              [projectId]: {
                ...prev[projectId],
                analysis: latestAnalysis.detailed_results,
              },
            }));
          }
        }
      }
    }

    // Route to the appropriate workspace and update URL to /project/:id
    if (projectType === 'redesign') {
      navigateTo('redesign', { projectId });
    } else if (projectType === 'native-ppt') {
      // Native PPT deck: reopen as a viewer (previews + download). Decks
      // saved after the outline started being persisted also carry
      // outline_slides + native_template_id, which re-enables the
      // regenerate-with-notes composer on reopen.
      const nd = freshData.nativePptData || projectAnalyses[projectId]?.nativePptData;
      const flowResult = {
        nativePpt: {
          title: nd?.metadata?.title || 'Presentation',
          previews: (nd?.slides || []).map(s => s?.preview_url || null),
          download_url: nd?.metadata?.download_url || '',
          slide_count: (nd?.slides || []).length,
          project_id: projectId,
          native_template_id: nd?.metadata?.native_template_id || null,
          outline_slides: nd?.metadata?.outline_slides || [],
          model: nd?.metadata?.model || null,
        },
        outline: { slides: nd?.metadata?.outline_slides || [], title: nd?.metadata?.title },
        questionnaireData: {},
        flowConfig: { generationMode: 'native-pptx', nativeTemplateId: nd?.metadata?.native_template_id || null },
        metadata: nd?.metadata || {},
        savedChatHistory: nd?.metadata?.chatHistory || [],
      };
      startWorkspaceForProject('agentic-flow', projectId, flowResult);
    } else if (projectType === 'create-ppt') {
      const savedData = freshData.createPPTData || projectAnalyses[projectId]?.createPPTData;
      const flowResult = {
        savedSlides: savedData?.slides || [],
        savedTitle: savedData?.title || 'Presentation',
        savedChatHistory: savedData?.metadata?.chatHistory || [],
        outline: { slides: [], title: savedData?.title },
        questionnaireData: savedData?.metadata?.questionnaireData || {},
        flowConfig: { generationMode: savedData?.metadata?.generationMode || savedData?.metadata?.generation_mode || 'html' },
        metadata: savedData?.metadata || {},
      };
      startWorkspaceForProject('agentic-flow', projectId, flowResult);
    } else if (projectType === 'infographic') {
      // Prefer freshData (from Supabase) over cached — cached may have corrupted/stripped html_content
      const savedData = freshData.infographicData || projectAnalyses[projectId]?.infographicData;
      const flowResult = {
        savedHtml: savedData?.html_content || '',
        savedImage: savedData?.image_content || '',
        savedTitle: savedData?.title || 'Infographic',
        savedChatHistory: savedData?.metadata?.chatHistory || [],
        outline: { slides: [], title: savedData?.title },
        questionnaireData: savedData?.metadata?.questionnaireData || {},
        flowConfig: { generationMode: savedData?.is_image_mode ? 'infographic-ai' : 'infographic-html' },
      };
      startWorkspaceForProject('infographic-flow', projectId, flowResult);
    } else if (projectType === 'document') {
      const savedData = freshData.documentData || projectAnalyses[projectId]?.documentData;
      const flowResult = {
        savedHtml: savedData?.html_content || '',
        savedTitle: savedData?.title || 'Document',
        savedChatHistory: savedData?.metadata?.chatHistory || [],
        outline: { slides: [], title: savedData?.title },
        questionnaireData: savedData?.metadata?.questionnaireData || {},
        flowConfig: { generationMode: 'document-html' },
      };
      startWorkspaceForProject('document-flow', projectId, flowResult);
    } else if (projectType === 'dashboard') {
      const savedData = freshData.dashboardData || projectAnalyses[projectId]?.dashboardData;
      const flowResult = {
        savedHtml: savedData?.html_content || '',
        savedTitle: savedData?.title || 'Dashboard',
        savedChatHistory: savedData?.metadata?.chatHistory || [],
        outline: { slides: [], title: savedData?.title },
        questionnaireData: savedData?.metadata?.questionnaireData || {},
        flowConfig: {
          generationMode: 'dashboard-html',
          dashboardTheme: savedData?.metadata?.dashboardTheme || 'dark-analytics',
          googleSheetUrl: savedData?.metadata?.googleSheetUrl || null,
          refreshInterval: savedData?.metadata?.refreshInterval || 60,
        },
      };
      startWorkspaceForProject('dashboard-flow', projectId, flowResult);
    } else if (projectType === 'sheets') {
      const savedData = freshData.sheetsData || projectAnalyses[projectId]?.sheetsData;
      const flowResult = {
        savedSheetData: savedData?.sheet_data || null,
        savedTitle: savedData?.title || 'Spreadsheet',
        savedChatHistory: savedData?.metadata?.chatHistory || [],
        questionnaireData: savedData?.metadata?.questionnaireData || {},
        flowConfig: { generationMode: 'sheet-data' },
      };
      startWorkspaceForProject('sheets-flow', projectId, flowResult);
    } else {
      navigateTo('analysis', { projectId });
    }
    setIsLoading(false);
  };

  const handleNewProject = () => {
    setSelectedProjectId(null);
    navigateTo('upload');
  };

  const handleNewRedesign = () => {
    setSelectedProjectId(null);
    navigateTo('redesign');
  };

  const handleNewCreatePPT = () => {
    setSelectedProjectId(null);
    navigateTo('create-ppt');
  };

  const handleCreatePPTComplete = async (payload) => {
    const nameFromPayload = payload.createPPTData?.title || 'Untitled Presentation';

    // Get first slide thumbnail for project card display
    const firstSlide = payload.createPPTData?.slides?.[0];
    const thumbnailUrl = normalizeImageUrl(firstSlide?.preview_url || null);

    // If project was pre-created by the background job endpoint, it's already in Supabase
    // — just update our local state, no need to create a new record
    const preCreatedId = payload.preCreatedProjectId;

    let newProjectId = preCreatedId || `project-${Date.now()}`;

    if (!preCreatedId && currentUser && currentUser.id) {
      const { data, error } = await supabaseHelpers.createProject({
        user_id: currentUser.id,
        name: nameFromPayload,
        client_name: 'Create PPT',
        status: 'completed',
        slide_count: payload.createPPTData?.metadata?.slide_count || 0,
        thumbnail_url: thumbnailUrl,
        analysis_data: {
          type: 'create-ppt',
          slides: payload.createPPTData?.slides,
          metadata: payload.createPPTData?.metadata
        },
      });

      if (error) {
        console.error('Error saving created PPT to Supabase:', error);
        toast.error('Failed to save presentation');
      } else if (data) {
        newProjectId = data.id;
        toast.success('Presentation saved!');
      }
    } else if (preCreatedId) {
      // Update the pre-created placeholder row with actual data
      if (currentUser && currentUser.id) {
        supabaseHelpers.updateProject(preCreatedId, {
          status: 'completed',
          slide_count: payload.createPPTData?.metadata?.slide_count || 0,
          thumbnail_url: thumbnailUrl,
          analysis_data: {
            type: 'create-ppt',
            slides: payload.createPPTData?.slides,
            metadata: payload.createPPTData?.metadata
          },
        }).catch(err => console.error('Error updating pre-created project:', err));
      }
      toast.success('Presentation saved!');
    }

    // Update or insert into projects list
    // If pre-created, replace the skeleton card; otherwise prepend new card
    const newProject = {
      id: newProjectId,
      name: nameFromPayload,
      client: 'Create PPT',
      status: 'completed',
      lastAnalyzed: new Date().toISOString(),
      owner: 'You',
      slideCount: payload.createPPTData?.metadata?.slide_count || 0,
      thumbnail: thumbnailUrl,
      score: null,
      type: 'create-ppt',
    };

    setProjects((prev) => {
      const idx = prev.findIndex(p => p.id === newProjectId);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = newProject;
        return updated;
      }
      return [newProject, ...prev];
    });
    setSelectedProjectId(newProjectId);

    // Store in projectAnalyses
    setProjectAnalyses((prev) => ({
      ...prev,
      [newProjectId]: {
        createPPTData: payload.createPPTData,
      },
    }));

    // Stay in workspace — don't navigate away on save
    // User can go back to dashboard manually via Back button
  };

  const handleRedesignComplete = async (payload) => {
    const nameFromPayload = payload.redesignData?.metadata?.file_name || 'Untitled Redesign';

    // Get first slide thumbnail for project card display
    const firstSlide = payload.redesignData?.slides?.[0];
    const thumbnailUrl = normalizeImageUrl(firstSlide?.thumbnail_url || firstSlide?.lazy_thumbnail_url || null);

    // Save project to Supabase
    let newProjectId = `project-${Date.now()}`;

    if (currentUser && currentUser.id) {
      const { data, error } = await supabaseHelpers.createProject({
        user_id: currentUser.id,
        name: nameFromPayload,
        client_name: 'PowerPoint Redesign',
        status: 'completed',
        slide_count: payload.redesignData?.metadata?.slide_count || 0,
        thumbnail_url: thumbnailUrl,
        redesign_data: payload.redesignData,
      });

      if (error) {
        console.error('Error saving redesign to Supabase:', error);
        toast.error('Failed to save redesign');
      } else if (data) {
        newProjectId = data.id;
        toast.success('PowerPoint Redesign saved!');
      }
    }

    const newProject = {
      id: newProjectId,
      name: nameFromPayload,
      client: 'PowerPoint Redesign',
      status: 'completed',
      lastAnalyzed: new Date().toISOString(),
      owner: 'You',
      slideCount: payload.redesignData?.metadata?.slide_count || 0,
      thumbnailUrl: thumbnailUrl,
      score: null,
      type: 'redesign', // Mark as redesign project
    };

    setProjects((prev) => [newProject, ...prev]);
    setSelectedProjectId(newProjectId);

    // Store in projectAnalyses
    setProjectAnalyses((prev) => ({
      ...prev,
      [newProjectId]: {
        redesignData: payload.redesignData,
      },
    }));

    // Stay on redesign view to show results (don't navigate away)
    // User can click "Back to Projects" when ready
  };

  const handleUploadComplete = async (uploadData = {}) => {
    const nameFromFile = uploadData.filename
      ? uploadData.filename.replace(/\.[^/.]+$/, '')
      : 'Untitled Presentation';

    // Save project to Supabase
    let newProjectId = `project-${Date.now()}`;

    if (currentUser && currentUser.id) {
      const { data, error } = await supabaseHelpers.createProject({
        user_id: currentUser.id,
        name: nameFromFile,
        client_name: uploadData.audienceInfo?.researchClient || 'Client TBD',
        status: 'in_progress',
        slide_count: 0,
      });

      if (error) {
        console.error('Error creating project in Supabase:', error);
        toast.error('Failed to save project');
      } else if (data) {
        newProjectId = data.id;
        toast.success('Project saved!');
      }
    }

    const newProject = {
      id: newProjectId,
      name: nameFromFile,
      client: uploadData.audienceInfo?.researchClient || 'Client TBD',
      status: 'in-progress',
      lastAnalyzed: null,
      owner: 'You',
      slideCount: null,
      score: null,
      type: 'analysis', // Mark as analysis project
      uploadedFile: uploadData.file, // Store the file object
      uploadMetadata: uploadData, // Store upload metadata including audienceInfo
    };

    setProjects((prev) => [newProject, ...prev]);
    setProjectAnalyses((prev) => ({
      ...prev,
      [newProjectId]: {
        ...(prev[newProjectId] || {}),
        analysis: prev[newProjectId]?.analysis ?? null,
        uploadMetadata: uploadData,
        file: uploadData.file, // Store file in analyses too
      },
    }));

    setSelectedProjectId(newProjectId);
    navigateTo('analysis', { projectId: newProjectId });
  };

  const handleBackToProjectsFromUpload = () => {
    navigateTo('dashboard');
  };

  const handleAnalysisComplete = async (projectId, payload) => {
    // Handle re-analyze request
    if (payload.reAnalyze) {
      // Update project with new audience info
      setProjects((prev) =>
        prev.map((project) => {
          if (project.id !== projectId) return project;
          return {
            ...project,
            uploadMetadata: {
              ...project.uploadMetadata,
              audienceInfo: payload.audienceInfo,
            },
          };
        })
      );

      // Clear analysis so AnalysisWorkspace shows the upload tool with initialFile
      setProjectAnalyses((prev) => ({
        ...prev,
        [projectId]: null,
      }));

      toast.info('Re-analyzing presentation with new settings...');

      // Return early - component will re-render with no analysis
      // This triggers AnalysisWorkspace to show DesignAnalysisTool with initialFile
      // The tool will auto-analyze with the stored file
      return;
    }

    setProjectAnalyses((prev) => ({
      ...prev,
      [projectId]: {
        ...(prev[projectId] || {}),
        ...payload,
      },
    }));

    // Save analysis results to Supabase
    if (currentUser && currentUser.id && payload.analysis) {
      const { error: analysisError } = await supabaseHelpers.saveAnalysisResult({
        project_id: projectId,
        analysis_type: 'design_analysis',
        overall_score: payload.analysis?.presentationScore?.overall || null,
        detailed_results: payload.analysis,
        ai_model_used: 'gpt-5-mini',
      });

      if (analysisError) {
        console.error('Error saving analysis:', analysisError);
      }
    }

    setProjects((prev) =>
      prev.map((project) => {
        if (project.id !== projectId) return project;
        const updated = { ...project };
        if (payload.fileName) {
          updated.name = payload.fileName;
        }
        updated.status = 'completed';
        updated.lastAnalyzed = new Date().toISOString();
        const slideCount =
          payload.parsedContent?.slideCount ??
          payload.analysis?.slide_count ??
          payload.analysis?.slideCount ??
          project.slideCount;
        if (slideCount) {
          updated.slideCount = slideCount;
        }
        const score =
          payload.analysis?.presentationScore?.overall ??
          payload.analysis?.overallScore ??
          project.score;
        if (score) {
          updated.score = score;
        }
        // Add thumbnail from first slide (already a data URI)
        const firstSlide = payload.analysis?.slides?.[0] || payload.parsedContent?.slides?.[0];
        if (firstSlide?.thumbnail_url) {
          updated.thumbnail = normalizeImageUrl(firstSlide.thumbnail_url);
        }

        // Update project in Supabase
        if (currentUser && currentUser.id) {
          supabaseHelpers.updateProject(projectId, {
            status: 'completed',
            last_analyzed: updated.lastAnalyzed,
            slide_count: updated.slideCount,
            thumbnail_url: updated.thumbnail,
          }).catch(err => console.error('Error updating project:', err));
        }

        return updated;
      })
    );
  };

  const isInWorkspace = activeWorkspace !== null;

  // Browser tab close / refresh guard
  useEffect(() => {
    if (!isInWorkspace) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isInWorkspace]);

  const handleNavigation = (page) => {
    setActiveWorkspace(null);
    setHideGlobalNav(false);
    if (page === 'projects' || page === 'dashboard') {
      navigateTo('dashboard');
      setSelectedProjectId(null);
      return;
    }
    navigateTo(page);
  };

  const handleLogout = async () => {
    // Sign out from Supabase
    await supabaseHelpers.signOut();
    // Clear remember me flag
    localStorage.removeItem('prezlab_remember_me');
    localStorage.removeItem('prezlab_selected_project');
    // Reset state
    setCurrentUser(null);
    setProjects([]);
    navigateTo('login');
    setSelectedProjectId(null);
    toast.success('Signed out successfully');
  };

  const handleBackToProjects = () => {
    setActiveWorkspace(null);
    setHideGlobalNav(false);
    navigateTo('dashboard');
    setSelectedProjectId(null);
    setInitialFile(null);
    setInitialPrompt('');
  };

  const handleDeleteProject = async (projectId) => {
    if (currentUser && currentUser.id) {
      const { error } = await supabaseHelpers.deleteProject(projectId);
      if (error) {
        console.error('Error deleting project:', error);
        toast.error('Failed to delete project');
        return;
      }
      toast.success('Project deleted');
    }

    // Remove from local state
    setProjects((prev) => prev.filter(p => p.id !== projectId));
    setProjectAnalyses((prev) => {
      const updated = { ...prev };
      delete updated[projectId];
      return updated;
    });

    // If we deleted the currently selected project, go back to dashboard
    if (selectedProjectId === projectId) {
      setSelectedProjectId(null);
      navigateTo('dashboard');
    }
  };

  const handleChatToggle = () => {
    setIsChatOpen((prev) => !prev);
    if (!isChatOpen && isChatExpanded) {
      setIsChatExpanded(false);
    }
  };

  const handleChatClose = () => {
    setIsChatOpen(false);
    setIsChatExpanded(false);
  };

  const handleChatToggleExpand = () => {
    setIsChatExpanded((prev) => !prev);
  };

  const handleToggleSidebar = useCallback(() => {
    // If sidebar is force-collapsed by workspace mode, expanding it should clear that override
    setHideGlobalNav(false);
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('prezlab_sidebar_collapsed', String(next)); } catch {}
      return next;
    });
  }, []);

  // Helper: create handler that resets state and navigates to a workspace
  const [initialPrompt, setInitialPrompt] = useState('');
  const [initialFile, setInitialFile] = useState(null);

  // Open a workspace (single per browser tab)
  const openWorkspace = useCallback((flow, prompt = '', file = null, projectId = null, savedFlowResult = null, initialAnswers = null) => {
    const label = TAB_TYPE_LABELS[flow] || 'Workspace';
    // `file` may be a single File (legacy) or an array of Files (multi-upload
    // from the landing). Normalize to an array; keep initialFile = first for any
    // component that only reads the single prop.
    const filesArr = Array.isArray(file) ? file.filter(Boolean) : (file ? [file] : []);
    setActiveWorkspace({ type: flow, label, initialPrompt: prompt, initialFile: filesArr[0] || null, initialFiles: filesArr, projectId, savedFlowResult, initialAnswers });
    // Keep the sidebar open when a workspace/questionnaire opens. The fullscreen
    // slide/doc editor still collapses it independently via its onFullscreen prop.
    setHideGlobalNav(false);
    navigateTo(flow, { projectId: projectId || undefined });
  }, [navigateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeWorkspace = useCallback(() => {
    setActiveWorkspace(null);
    setHideGlobalNav(false);
    setSelectedProjectId(null);
    navigateTo('dashboard');
  }, [navigateTo]);

  const startWorkspace = useCallback((flow, prompt = '', file = null, initialAnswers = null) => {
    setSelectedProjectId(null);
    setAgenticFlowResult(null);
    openWorkspace(flow, prompt, file, null, null, initialAnswers);
  }, [openWorkspace]);

  // Open a saved project workspace
  const startWorkspaceForProject = useCallback((flow, projectId, savedFlowResult) => {
    setSelectedProjectId(projectId);
    openWorkspace(flow, '', null, projectId, savedFlowResult);
  }, [openWorkspace]);

  return (
    <ThemeProvider>
      <ToastProvider />
      {(() => {
        // Email-confirmation gate. Verification is required from the start: any
        // signed-in user whose email isn't confirmed is held behind the
        // confirm-email screen until they verify (no grace period). OAuth/social
        // accounts arrive already confirmed and pass straight through. Computed
        // fresh from email_confirmed_at on every render, so it can't be bypassed
        // by refreshing or signing in again.
        if (!currentUser) return null;
        if (currentUser.email_confirmed_at) return null;
        return (
          <EmailConfirmationLock
            user={currentUser}
            onSignOut={handleLogout}
            onConfirmed={(refreshed) => setCurrentUser(refreshed)}
          />
        );
      })() || (
      <>
      {currentState === 'loading' ? (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      ) : currentState === 'login' ? (
        <LoginPage onLogin={handleLogin} />
      ) : currentState === 'generation-progress' ? (
        <GenerationProgressView
          projectId={selectedProjectId}
          onComplete={() => {
            // Re-route through handleProjectSelect so the regular workspace
            // picks up the now-completed slides.
            if (selectedProjectId) handleProjectSelect(selectedProjectId);
          }}
          onBack={() => navigateTo('dashboard')}
          onRetry={() => navigateTo('upload')}
        />
      ) : currentState === 'admin' ? (
        <AdminDashboard onLogout={() => handleNavigation('login')} />
      ) : currentState === 'landing-b2c' ? (
        <LandingB2C />
      ) : currentState === 'landing-b2b' ? (
        <LandingB2B />
      ) : currentState === 'redeem' ? (
        <RedeemTrialPage
          currentUser={currentUser}
          onLogin={handleLogin}
          onRedeemed={() => fetchUsage(currentUser?.id, currentUser?.email)}
        />
      ) : (
        <div className="min-h-screen bg-background">
          {/* Name Prompt Modal */}
          <NamePromptModal
            isOpen={showNamePrompt}
            onNameSubmit={handleNameSubmit}
          />

          {/* Password Reset Modal */}
          {showPasswordReset && (
            <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4">
              <div className="bg-card rounded-xl border border-border shadow-2xl w-full max-w-sm p-6">
                <h2 className="text-lg font-bold text-foreground mb-1">Set New Password</h2>
                <p className="text-sm text-muted-foreground mb-5">Enter your new password below.</p>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const newPw = e.target.elements.newPassword.value;
                  const confirmPw = e.target.elements.confirmPassword.value;
                  if (newPw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
                  if (newPw !== confirmPw) { toast.error('Passwords do not match'); return; }
                  try {
                    const { error } = await supabase.auth.updateUser({ password: newPw });
                    if (error) throw error;
                    toast.success('Password updated successfully!');
                    setShowPasswordReset(false);
                  } catch (err) {
                    toast.error(err.message || 'Failed to update password');
                  }
                }}>
                  <div className="space-y-3 mb-5">
                    <input name="newPassword" type="password" placeholder="New password" required minLength={6}
                      className="w-full h-11 px-3 rounded-lg border border-border bg-input text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent/30" />
                    <input name="confirmPassword" type="password" placeholder="Confirm password" required minLength={6}
                      className="w-full h-11 px-3 rounded-lg border border-border bg-input text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent/30" />
                  </div>
                  <button type="submit" className="w-full h-11 rounded-lg bg-gradient-to-r from-accent to-accent/90 text-accent-foreground font-medium text-sm hover:opacity-90 transition-opacity">
                    Update Password
                  </button>
                </form>
              </div>
            </div>
          )}

          <AppSidebar
            settingsOpen={settingsOpen}
            onSettingsOpenChange={setSettingsOpen}
            currentPage={currentState}
            collapsed={hideGlobalNav ? true : sidebarCollapsed}
            onToggleCollapse={handleToggleSidebar}
            onNavigate={handleNavigation}
            onLogout={handleLogout}
            onStartPresentation={() => startWorkspace('agentic-flow')}
            onStartProposal={() => startWorkspace('proposal-flow')}
            onStartInfographic={() => startWorkspace('infographic-flow')}
            onStartDocument={() => startWorkspace('document-flow')}
            onStartDashboard={() => startWorkspace('dashboard-flow')}
            onStartSheets={() => startWorkspace('sheets-flow')}
            onStartPlanning={() => startWorkspace('planning-flow')}
            userId={currentUser?.id}
            recentProjects={projects}
            onProjectSelect={handleProjectSelect}
            onOpenFolder={(folderId) => { setProjectsInitialFolder(folderId); handleNavigation('projects-list'); }}
            usageData={usageData}
            userName={userName}
            userEmail={currentUser?.email}
            hidden={currentState === 'shared-report' || currentState === 'shared-content' || currentState === 'shared-brand' || currentState === 'landing-b2c' || currentState === 'landing-b2b'}
          />

          <main className={`relative transition-all duration-300 ${(currentState === 'shared-report' || currentState === 'shared-content' || currentState === 'shared-brand' || currentState === 'landing-b2c' || currentState === 'landing-b2b') ? 'ml-0' : hideGlobalNav ? 'ml-[68px]' : sidebarCollapsed ? 'ml-[68px]' : 'ml-sidebar'}`}>
            {!activeWorkspace && currentState === 'dashboard' && (
              <Dashboard
                userName={userName}
                projects={projects}
                isLoading={isLoading}
                onProjectSelect={handleProjectSelect}
                onDeleteProject={handleDeleteProject}
                onStartPresentation={(prompt, file, ia) => startWorkspace('agentic-flow', prompt, file, ia)}
                onStartProposal={(prompt, file, ia) => startWorkspace('proposal-flow', prompt, file, ia)}
                onStartInfographic={(prompt, file, ia) => startWorkspace('infographic-flow', prompt, file, ia)}
                onStartDocument={(prompt, file, ia) => startWorkspace('document-flow', prompt, file, ia)}
                onStartDashboard={(prompt, file, ia) => startWorkspace('dashboard-flow', prompt, file, ia)}
                onStartSheets={(prompt, file, ia) => startWorkspace('sheets-flow', prompt, file, ia)}
                onStartPlanning={(prompt, file, ia) => startWorkspace('planning-flow', prompt, file, ia)}
                userId={currentUser?.id}
                usageData={usageData}
              />
            )}

            {/* Active workspace — single instance per browser tab */}
            {activeWorkspace && (() => {
              const ws = activeWorkspace;
              const wsBack = closeWorkspace;
              // Round 74: the intent classifier detected the prompt asks for a
              // different deliverable than the active mode — remount the right
              // workspace with the prompt carried over (auto-submits on mount).
              const switchWorkspaceType = (newType, carryPrompt) => {
                // Render branches match FLOW-suffixed types only (presentation
                // = 'agentic-flow') — a bare type would blank the workspace.
                const flowTypes = { presentation: 'agentic-flow', document: 'document-flow', infographic: 'infographic-flow', dashboard: 'dashboard-flow', sheets: 'sheets-flow' };
                const labels = { presentation: 'Presentation', document: 'Document', infographic: 'Infographic', dashboard: 'Dashboard', sheets: 'Sheets' };
                const flowType = flowTypes[newType];
                if (!flowType) return;
                setActiveWorkspace(prev => prev ? {
                  ...prev, type: flowType, label: labels[newType],
                  initialPrompt: carryPrompt || '', projectId: undefined,
                  savedFlowResult: undefined, initialAnswers: null,
                } : prev);
                try { navigateTo(flowType, {}); } catch { /* URL best-effort */ }
              };
              const updateWorkspaceLabel = (label) => setActiveWorkspace(prev => prev ? { ...prev, label } : prev);
              const updateWorkspaceProject = (newProjectId) => {
                if (!newProjectId || ws.projectId) return;
                setActiveWorkspace(prev => prev ? { ...prev, projectId: newProjectId } : prev);
                navigateTo(ws.type, { projectId: newProjectId });
              };

              return (
                <>
                  {ws.type === 'planning-flow' && (
                    <PlanningWorkspace
                      initialPrompt={ws.initialPrompt}
                      initialFile={ws.initialFile}
                      initialFiles={ws.initialFiles}
                      initialAnswers={ws.initialAnswers || null}
                      onBack={wsBack}
                      onComplete={(payload) => {
                        updateWorkspaceProject(payload.projectId);
                        if (payload.createPPTData) {
                          updateWorkspaceLabel(payload.createPPTData?.title || 'Slides');
                          handleCreatePPTComplete(payload);
                        } else if (payload.documentData) {
                          updateWorkspaceLabel(payload.documentData?.title || 'Document');
                          const newProject = {
                            id: payload.projectId,
                            name: payload.documentData?.title || 'Untitled Document',
                            client: 'Document',
                            status: 'completed',
                            lastAnalyzed: new Date().toISOString(),
                            owner: 'You',
                            slideCount: payload.documentData?.section_count || 1,
                            thumbnail: normalizeImageUrl(payload.preview_url) || null,
                            score: null,
                            type: 'document',
                          };
                          setProjects((prev) => [newProject, ...prev]);
                          setSelectedProjectId(payload.projectId);
                          setProjectAnalyses((prev) => ({ ...prev, [payload.projectId]: { documentData: payload.documentData } }));
                        } else if (payload.infographicData) {
                          updateWorkspaceLabel(payload.infographicData?.title || 'Infographic');
                          const newProject = {
                            id: payload.projectId,
                            name: payload.infographicData?.title || 'Untitled Infographic',
                            client: 'Infographic',
                            status: 'completed',
                            lastAnalyzed: new Date().toISOString(),
                            owner: 'You',
                            slideCount: payload.infographicData?.section_count || 1,
                            thumbnail: normalizeImageUrl(payload.preview_url) || null,
                            score: null,
                            type: 'infographic',
                          };
                          setProjects((prev) => [newProject, ...prev]);
                          setSelectedProjectId(payload.projectId);
                          setProjectAnalyses((prev) => ({ ...prev, [payload.projectId]: { infographicData: payload.infographicData } }));
                        } else if (payload.dashboardData) {
                          updateWorkspaceLabel(payload.dashboardData?.title || 'Dashboard');
                          const newProject = {
                            id: payload.projectId,
                            name: payload.dashboardData?.title || 'Untitled Dashboard',
                            client: 'Dashboard',
                            status: 'completed',
                            lastAnalyzed: new Date().toISOString(),
                            owner: 'You',
                            slideCount: payload.dashboardData?.panel_count || 1,
                            thumbnail: normalizeImageUrl(payload.preview_url) || null,
                            score: null,
                            type: 'dashboard',
                          };
                          setProjects((prev) => [newProject, ...prev]);
                          setSelectedProjectId(payload.projectId);
                          setProjectAnalyses((prev) => ({ ...prev, [payload.projectId]: { dashboardData: payload.dashboardData } }));
                        } else if (payload.sheetsData) {
                          updateWorkspaceLabel(payload.sheetsData?.title || 'Spreadsheet');
                          const newProject = {
                            id: payload.projectId,
                            name: payload.sheetsData?.title || 'Untitled Spreadsheet',
                            client: 'Sheets',
                            status: 'completed',
                            lastAnalyzed: new Date().toISOString(),
                            owner: 'You',
                            slideCount: 1,
                            thumbnail: null,
                            score: null,
                            type: 'sheets',
                          };
                          setProjects((prev) => [newProject, ...prev]);
                          setSelectedProjectId(payload.projectId);
                          setProjectAnalyses((prev) => ({ ...prev, [payload.projectId]: { sheetsData: payload.sheetsData } }));
                        }
                      }}
                      onUpdate={(projectId, data) => {
                        const title = data?.title || data?.createPPTData?.title;
                        if (title) updateWorkspaceLabel(title);
                        if (data?.createPPTData || data?.slides) {
                          const createPPTData = data?.createPPTData || data;
                          setProjectAnalyses((prev) => ({ ...prev, [projectId]: { ...prev[projectId], createPPTData } }));
                        } else if (data?.html_content !== undefined && data?.type !== 'dashboard') {
                          setProjectAnalyses((prev) => ({ ...prev, [projectId]: { ...prev[projectId], documentData: data } }));
                        } else {
                          setProjectAnalyses((prev) => ({ ...prev, [projectId]: { ...prev[projectId], createPPTData: data } }));
                        }
                      }}
                      onFullscreen={setHideGlobalNav}
                      userId={currentUser?.id}
                      existingProjectId={ws.projectId || null}
                      usageData={usageData}
                      onUsageRefresh={() => fetchUsage(currentUser?.id, currentUser?.email)}
                    />
                  )}

                  {(ws.type === 'agentic-flow' || ws.type === 'proposal-flow') && (
                    <UnifiedWorkspace
                      onSwitchProjectType={switchWorkspaceType}
                      proposalMode={ws.type === 'proposal-flow'}
                      projectType="presentation"
                      initialPrompt={ws.initialPrompt}
                      initialFile={ws.initialFile}
                      initialFiles={ws.initialFiles}
                      initialAnswers={ws.initialAnswers || null}
                      onBack={wsBack}
                      onOutlineReady={(outline, questionnaireData, flowConfig) => {
                        setAgenticFlowResult({ outline, questionnaireData, flowConfig });
                      }}
                      onComplete={(payload) => { updateWorkspaceProject(payload.projectId); updateWorkspaceLabel(payload.createPPTData?.title || 'Slides'); handleCreatePPTComplete(payload); }}
                      onUpdate={(projectId, createPPTData) => {
                        if (createPPTData?.title) updateWorkspaceLabel(createPPTData.title);
                        setProjectAnalyses((prev) => ({ ...prev, [projectId]: { ...prev[projectId], createPPTData } }));
                      }}
                      onProjectCreated={(projectId, name, meta) => {
                        // Bookmarkable/refresh-safe URL: swap to /project/<id> once
                        // the deck's project row exists. replaceState avoids
                        // remounting the workspace (which would kill the SSE stream).
                        // meta lets non-HTML engines (native PPT) label their
                        // Recents entry correctly instead of the create-ppt skeleton.
                        setActiveWorkspace(prev => prev ? { ...prev, projectId } : prev);
                        setSelectedProjectId(projectId);
                        // Upsert: a regeneration re-fires with the same id and
                        // fresher meta (thumbnail/slide count) — update in place.
                        setProjects(prev => {
                          const entry = {
                            id: projectId, name,
                            client: meta?.client || 'Create PPT',
                            status: meta?.status || 'generating',
                            lastAnalyzed: new Date().toISOString(), owner: 'You',
                            slideCount: meta?.slideCount || 0,
                            thumbnail: meta?.thumbnail || null, score: null,
                            type: meta?.type || 'create-ppt',
                          };
                          return prev.some(p => p.id === projectId)
                            ? prev.map(p => (p.id === projectId ? { ...p, ...entry } : p))
                            : [entry, ...prev];
                        });
                        try { window.history.replaceState(null, '', `/project/${projectId}`); } catch (_) { /* non-critical */ }
                      }}
                      onFullscreen={setHideGlobalNav}
                      userId={currentUser?.id}
                      existingProjectId={ws.projectId || null}
                      savedFlowResult={ws.savedFlowResult || null}
                      usageData={usageData}
                      onUsageRefresh={() => fetchUsage(currentUser?.id, currentUser?.email)}
                    />
                  )}

                  {ws.type === 'infographic-flow' && (
                    <UnifiedWorkspace
                      onSwitchProjectType={switchWorkspaceType}
                      projectType="infographic"
                      initialPrompt={ws.initialPrompt}
                      initialFile={ws.initialFile}
                      initialFiles={ws.initialFiles}
                      initialAnswers={ws.initialAnswers || null}
                      onBack={wsBack}
                      onOutlineReady={(outline, questionnaireData, flowConfig) => {
                        setAgenticFlowResult({ outline, questionnaireData, flowConfig });
                      }}
                      onComplete={(payload) => {
                        updateWorkspaceProject(payload.projectId);
                        updateWorkspaceLabel(payload.infographicData?.title || 'Infographic');
                        const newProject = {
                          id: payload.projectId,
                          name: payload.infographicData?.title || 'Untitled Infographic',
                          client: 'Infographic',
                          status: 'completed',
                          lastAnalyzed: new Date().toISOString(),
                          owner: 'You',
                          slideCount: payload.infographicData?.section_count || 1,
                          thumbnail: normalizeImageUrl(payload.preview_url) || null,
                          score: null,
                          type: 'infographic',
                        };
                        setProjects((prev) => [newProject, ...prev]);
                        setSelectedProjectId(payload.projectId);
                        setProjectAnalyses((prev) => ({ ...prev, [payload.projectId]: { infographicData: payload.infographicData } }));
                      }}
                      onUpdate={(pid, infographicData) => {
                        if (infographicData?.title) updateWorkspaceLabel(infographicData.title);
                        setProjectAnalyses((prev) => ({ ...prev, [pid]: { ...prev[pid], infographicData } }));
                      }}
                      onFullscreen={setHideGlobalNav}
                      userId={currentUser?.id}
                      existingProjectId={ws.projectId || null}
                      savedFlowResult={ws.savedFlowResult || null}
                      usageData={usageData}
                      onUsageRefresh={() => fetchUsage(currentUser?.id, currentUser?.email)}
                    />
                  )}

                  {ws.type === 'document-flow' && (
                    <UnifiedWorkspace
                      onSwitchProjectType={switchWorkspaceType}
                      projectType="document"
                      initialPrompt={ws.initialPrompt}
                      initialFile={ws.initialFile}
                      initialFiles={ws.initialFiles}
                      initialAnswers={ws.initialAnswers || null}
                      onBack={wsBack}
                      onOutlineReady={(outline, questionnaireData, flowConfig) => {
                        setAgenticFlowResult({ outline, questionnaireData, flowConfig });
                      }}
                      onComplete={(payload) => {
                        updateWorkspaceProject(payload.projectId);
                        updateWorkspaceLabel(payload.documentData?.title || 'Document');
                        const newProject = {
                          id: payload.projectId,
                          name: payload.documentData?.title || 'Untitled Document',
                          client: 'Document',
                          status: 'completed',
                          lastAnalyzed: new Date().toISOString(),
                          owner: 'You',
                          slideCount: payload.documentData?.section_count || 1,
                          thumbnail: normalizeImageUrl(payload.preview_url) || null,
                          score: null,
                          type: 'document',
                        };
                        setProjects((prev) => [newProject, ...prev]);
                        setSelectedProjectId(payload.projectId);
                        setProjectAnalyses((prev) => ({ ...prev, [payload.projectId]: { documentData: payload.documentData } }));
                      }}
                      onUpdate={(pid, documentData) => {
                        if (documentData?.title) updateWorkspaceLabel(documentData.title);
                        setProjectAnalyses((prev) => ({ ...prev, [pid]: { ...prev[pid], documentData } }));
                      }}
                      onFullscreen={setHideGlobalNav}
                      userId={currentUser?.id}
                      existingProjectId={ws.projectId || null}
                      savedFlowResult={ws.savedFlowResult || null}
                      usageData={usageData}
                      onUsageRefresh={() => fetchUsage(currentUser?.id, currentUser?.email)}
                    />
                  )}

                  {ws.type === 'dashboard-flow' && (
                    <UnifiedWorkspace
                      onSwitchProjectType={switchWorkspaceType}
                      projectType="dashboard"
                      initialPrompt={ws.initialPrompt}
                      initialFile={ws.initialFile}
                      initialFiles={ws.initialFiles}
                      initialAnswers={ws.initialAnswers || null}
                      onBack={wsBack}
                      onOutlineReady={(outline, questionnaireData, flowConfig) => {
                        setAgenticFlowResult({ outline, questionnaireData, flowConfig });
                      }}
                      onComplete={(payload) => {
                        updateWorkspaceProject(payload.projectId);
                        updateWorkspaceLabel(payload.dashboardData?.title || 'Dashboard');
                        const newProject = {
                          id: payload.projectId,
                          name: payload.dashboardData?.title || 'Untitled Dashboard',
                          client: 'Dashboard',
                          status: 'completed',
                          lastAnalyzed: new Date().toISOString(),
                          owner: 'You',
                          slideCount: payload.dashboardData?.panel_count || 1,
                          thumbnail: normalizeImageUrl(payload.preview_url) || null,
                          score: null,
                          type: 'dashboard',
                        };
                        setProjects((prev) => [newProject, ...prev]);
                        setSelectedProjectId(payload.projectId);
                        setProjectAnalyses((prev) => ({ ...prev, [payload.projectId]: { dashboardData: payload.dashboardData } }));
                      }}
                      onUpdate={(pid, dashboardData) => {
                        if (dashboardData?.title) updateWorkspaceLabel(dashboardData.title);
                        setProjectAnalyses((prev) => ({ ...prev, [pid]: { ...prev[pid], dashboardData } }));
                      }}
                      onFullscreen={setHideGlobalNav}
                      userId={currentUser?.id}
                      existingProjectId={ws.projectId || null}
                      savedFlowResult={ws.savedFlowResult || null}
                      usageData={usageData}
                      onUsageRefresh={() => fetchUsage(currentUser?.id, currentUser?.email)}
                    />
                  )}

                  {ws.type === 'sheets-flow' && (
                    <UnifiedWorkspace
                      onSwitchProjectType={switchWorkspaceType}
                      projectType="sheets"
                      initialPrompt={ws.initialPrompt}
                      initialFile={ws.initialFile}
                      initialFiles={ws.initialFiles}
                      initialAnswers={ws.initialAnswers || null}
                      onBack={wsBack}
                      onOutlineReady={(outline, questionnaireData, flowConfig) => {
                        setAgenticFlowResult({ outline, questionnaireData, flowConfig });
                      }}
                      onComplete={(payload) => {
                        updateWorkspaceProject(payload.projectId);
                        updateWorkspaceLabel(payload.sheetsData?.title || 'Spreadsheet');
                        const newProject = {
                          id: payload.projectId,
                          name: payload.sheetsData?.title || 'Untitled Spreadsheet',
                          client: 'Sheets',
                          status: 'completed',
                          lastAnalyzed: new Date().toISOString(),
                          owner: 'You',
                          slideCount: 1,
                          thumbnail: null,
                          score: null,
                          type: 'sheets',
                        };
                        setProjects((prev) => [newProject, ...prev]);
                        setSelectedProjectId(payload.projectId);
                        setProjectAnalyses((prev) => ({ ...prev, [payload.projectId]: { sheetsData: payload.sheetsData } }));
                      }}
                      onUpdate={(pid, sheetsData) => {
                        if (sheetsData?.title) updateWorkspaceLabel(sheetsData.title);
                        setProjectAnalyses((prev) => ({ ...prev, [pid]: { ...prev[pid], sheetsData } }));
                      }}
                      onFullscreen={setHideGlobalNav}
                      userId={currentUser?.id}
                      existingProjectId={ws.projectId || null}
                      savedFlowResult={ws.savedFlowResult || null}
                      usageData={usageData}
                      onUsageRefresh={() => fetchUsage(currentUser?.id, currentUser?.email)}
                    />
                  )}
                </>
              );
            })()}

            {/* Non-workspace pages — only show when no tab is active */}
            {!activeWorkspace && currentState === 'upload' && (
              <UploadPage
                onBack={handleBackToProjectsFromUpload}
                onComplete={handleUploadComplete}
              />
            )}

            {!activeWorkspace && currentState === 'analysis' && selectedProject && (
              <AnalysisWorkspace
                project={selectedProject}
                analysis={projectAnalyses[selectedProject.id]?.analysis}
                onBack={handleBackToProjects}
                onAnalysisComplete={(payload) =>
                  handleAnalysisComplete(selectedProject.id, payload)
                }
              />
            )}

            {!activeWorkspace && currentState === 'redesign' && (
              <RedesignWorkspace
                project={selectedProject}
                redesignData={selectedProject ? projectAnalyses[selectedProject.id]?.redesignData : null}
                onBack={handleBackToProjects}
                onRedesignComplete={handleRedesignComplete}
              />
            )}

            {!activeWorkspace && currentState === 'create-ppt' && (
              <CreatePPTWorkspace
                onBack={() => { setHideGlobalNav(false); handleBackToProjects(); }}
                onComplete={handleCreatePPTComplete}
                onFullscreen={setHideGlobalNav}
                onUpdate={(projectId, createPPTData) => {
                  setProjectAnalyses((prev) => ({
                    ...prev,
                    [projectId]: { ...prev[projectId], createPPTData }
                  }));
                }}
                onProjectCreated={(projectId, name) => {
                  // Add skeleton card so dashboard shows progress if user navigates away
                  setProjects(prev => [{
                    id: projectId,
                    name,
                    client: 'Create PPT',
                    status: 'generating',
                    lastAnalyzed: new Date().toISOString(),
                    owner: 'You',
                    slideCount: 0,
                    thumbnail: null,
                    score: null,
                    type: 'create-ppt',
                  }, ...prev]);
                  // Swap URL to /project/<id> so refresh resumes on the project
                  // instead of bouncing the user back to /create/presentation.
                  // history.replaceState avoids triggering React Router (which
                  // would unmount the workspace and kill the SSE stream).
                  setSelectedProjectId(projectId);
                  try {
                    window.history.replaceState(null, '', `/project/${projectId}`);
                  } catch (_) { /* swallow — non-critical UX nicety */ }
                }}
                userId={currentUser?.id}
                existingProject={selectedProjectId ? {
                  ...projectAnalyses[selectedProjectId]?.createPPTData,
                  ...(projects.find(p => p.id === selectedProjectId)?.analysis_data?.createPPTData || {}),
                  projectId: selectedProjectId,
                  pptProjectId: projectAnalyses[selectedProjectId]?.createPPTData?.projectId || null,
                } : null}
                preloadedOutline={agenticFlowResult}
              />
            )}

            {!activeWorkspace && currentState === 'enhance' && <EnhancePage />}

            {!activeWorkspace && currentState === 'ensure' && <EnsurePage />}

            {!activeWorkspace && currentState === 'knowledge' && <KnowledgeBasePage userId={currentUser?.id} />}

            {!activeWorkspace && currentState === 'brand-identities' && (
              <BrandIdentitiesPage
                userId={currentUser?.id}
                onBack={() => handleNavigation('dashboard')}
              />
            )}

            {!activeWorkspace && currentState === 'projects-list' && (
              <ProjectsPage
                userId={currentUser?.id}
                onBack={() => handleNavigation('dashboard')}
                onOpenDocument={handleProjectSelect}
                allDocuments={projects}
                initialFolderId={projectsInitialFolder}
                onInitialFolderConsumed={() => setProjectsInitialFolder(null)}
              />
            )}

            {!activeWorkspace && currentState === 'native-template-lab' && (
              <NativeTemplateLab userId={currentUser?.id} />
            )}

            {!activeWorkspace && currentState === 'recents' && (
              <RecentsPage
                projects={projects}
                isLoading={isLoading}
                onProjectSelect={handleProjectSelect}
                onDeleteProject={handleDeleteProject}
                onRenameProject={async (id, name) => {
                  // r149: owner rename — optimistic, reverted on failure
                  const prevName = projects.find(p => p.id === id)?.name;
                  setProjects(prev => prev.map(p => p.id === id ? { ...p, name } : p));
                  const { error } = await supabase.from('projects').update({ name }).eq('id', id);
                  if (error) {
                    setProjects(prev => prev.map(p => p.id === id ? { ...p, name: prevName } : p));
                  }
                }}
                onCreateNew={() => handleNavigation('dashboard')}
                userId={currentUser?.id}
                onLeft={(id) => setProjects(prev => prev.filter(p => p.id !== id))}
              />
            )}

            {/* r113b: the standalone account page is retired. Everything it
                showed is in Settings -> Account, and keeping a second copy is
                how a plan badge ends up disagreeing with itself. Old links
                still work — they open Settings rather than 404. */}
            {!activeWorkspace && currentState === 'profile' && (
              <ProfileRedirect onOpenSettings={() => { setSettingsOpen(true); handleNavigation('dashboard'); }} />
            )}

            {!activeWorkspace && currentState === 'shared-report' && <SharedReportView />}
            {!activeWorkspace && currentState === 'shared-content' && <SharedContentView />}
            {!activeWorkspace && currentState === 'shared-brand' && <SharedBrandView userId={currentUser?.id} />}
            {currentState === 'landing-b2c' && <LandingB2C />}
            {currentState === 'landing-b2b' && <LandingB2B />}
            {currentState === 'oauth-callback' && <OAuthCallbackPage />}
          </main>

          <AIChat
            isOpen={isChatOpen}
            onClose={handleChatClose}
            isExpanded={isChatExpanded}
            onToggleExpand={handleChatToggleExpand}
            analysis={selectedProject ? projectAnalyses[selectedProject.id]?.analysis : null}
            projects={projects}
            projectAnalyses={projectAnalyses}
          />
        </div>
      )}
      </>
      )}
    </ThemeProvider>
  );
}

export default App;
